import { X } from 'lucide-react';
﻿import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

const COA: Array<{
    code: string; name: string; type: string; subType: string;
    balance: number; ytd: number; status: string;
}> = [];

const JOURNAL_ENTRIES: Array<{
    id: string; date: string; description: string; type: string;
    status: string; debit: number; credit: number; preparedBy: string;
}> = [];

const typeColors: Record<string, string> = {
    asset: 'bg-info/20 text-info',
    liability: 'bg-destructive/20 text-destructive',
    equity: 'bg-primary/20 text-primary',
    income: 'bg-success/20 text-success',
    expense: 'bg-warning/20 text-warning',
};

const statusVariant: Record<string, 'success' | 'warning' | 'default' | 'primary'> = {
    posted: 'success',
    'pending-approval': 'warning',
    draft: 'default',
    approved: 'primary',
};

export function GeneralLedger() {
    const [activeTab, setActiveTab] = useState<'coa' | 'journal' | 'balance-sheet' | 'trial-balance'>('coa');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [showNewJE, setShowNewJE] = useState(false);
    const [jeLines, setJeLines] = useState<any[]>([]);

    const filteredCOA = typeFilter === 'all' ? COA : COA.filter(a => a.type === typeFilter);
    const totalDebit = jeLines.reduce((s, l) => s + l.debit, 0);
    const totalCredit = jeLines.reduce((s, l) => s + l.credit, 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">General <span className="text-foreground">Ledger</span></h1>
                    <p className="text-muted-foreground mt-1">Chart of Accounts · Journal Entries · Financial Statements</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="secondary" onClick={() => toast.success('Exporting COA...')}>Export</Button>
                    <Button variant="primary" onClick={() => setShowNewJE(true)}>+ Journal Entry</Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'coa', label: 'Chart of Accounts' },
                    { id: 'journal', label: 'Journal Entries' },
                    { id: 'trial-balance', label: 'Trial Balance' },
                    { id: 'balance-sheet', label: 'Balance Sheet' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {/* Chart of Accounts */}
            {activeTab === 'coa' && (
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
                        <h2 className="text-foreground font-display text-section-title">Standard Chart of Accounts</h2>
                        <div className="flex gap-2">
                            {['all', 'asset', 'liability', 'equity', 'income', 'expense'].map(t => (
                                <Button variant="unstyled" size="unstyled" key={t} onClick={() => setTypeFilter(t)}
                                    className={`px-3 py-1 text-xs font-semibold rounded-lg capitalize transition-all ${typeFilter === t ? 'bg-primary text-white' : 'bg-card text-muted-foreground hover:bg-card'}`}>
                                    {t}
                                </Button>
                            ))}
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                    <th className="text-left px-6 py-3">Account Code</th>
                                    <th className="text-left px-4 py-3">Account Name</th>
                                    <th className="text-center px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Sub-Type</th>
                                    <th className="text-right px-4 py-3">Current Balance</th>
                                    <th className="text-right px-4 py-3">YTD Balance</th>
                                    <th className="text-center px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-12">
                                {filteredCOA.map((acct) => (
                                    <tr key={acct.code} className="hover:bg-card/30 transition-colors cursor-pointer">
                                        <td className="px-6 py-3 font-mono text-sm font-semibold text-primary">{acct.code}</td>
                                        <td className="px-4 py-3 font-semibold text-foreground">{acct.name}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${typeColors[acct.type]}`}>{acct.type}</span>
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground text-sm">{acct.subType}</td>
                                        <td className={`px-4 py-3 text-right font-semibold font-mono ${acct.balance < 0 ? 'text-destructive' : 'text-foreground'}`}>
                                            {acct.balance < 0 ? `($${Math.abs(acct.balance).toLocaleString()})` : `$${acct.balance.toLocaleString()}`}
                                        </td>
                                        <td className={`px-4 py-3 text-right font-mono text-muted-foreground`}>${acct.ytd.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-center">
                                            <Badge variant={acct.status === 'active' ? 'success' : 'default'}>{acct.status}</Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {/* Journal Entries */}
            {activeTab === 'journal' && (
                <div className="space-y-4">
                    {/* New JE Form */}
                    {showNewJE && (
                        <Card className="border-primary/30 bg-primary/5">
                            <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
                                <h2 className="text-foreground font-display text-section-title">New Journal Entry</h2>
                                <Button variant="unstyled" size="unstyled" onClick={() => setShowNewJE(false)} className="text-muted-foreground hover:text-foreground text-xl"><X className="size-3.5" /></Button>
                            </CardHeader>
                            <CardContent className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                    {[
                                        { label: 'Entry Date', type: 'date', defaultValue: '2026-03-09' },
                                        { label: 'Posting Date', type: 'date', defaultValue: '2026-03-09' },
                                        { label: 'Reference', type: 'text', placeholder: 'Optional' },
                                        { label: 'Type', type: 'select' },
                                    ].map((f, i) => (
                                        <div key={i} className="space-y-1">
                                            <label className="text-xs font-semibold text-muted-foreground">{f.label}</label>
                                            {f.type === 'select' ? (
                                                <select className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
                                                    <option>Standard</option><option>Recurring</option><option>Reversing</option>
                                                </select>
                                            ) : (
                                                <Input type={f.type} defaultValue={f.defaultValue} placeholder={f.placeholder}
                                                    className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="mb-4">
                                    <label className="text-xs font-semibold text-muted-foreground">Description</label>
                                    <Input type="text" placeholder="e.g., March payroll processing" className="w-full mt-1 bg-background border border-border rounded-lg px-3 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
                                </div>
                                {/* Lines Table */}
                                <div className="overflow-x-auto rounded-lg border border-border">
                                    <table className="w-full text-sm">
                                        <thead className="bg-card/50">
                                            <tr className="text-sm text-muted-foreground font-semibold">
                                                <th className="text-left px-4 py-3">Account</th>
                                                <th className="text-left px-4 py-3">Description</th>
                                                <th className="text-right px-4 py-3">Debit</th>
                                                <th className="text-right px-4 py-3">Credit</th>
                                                <th className="px-3 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border-12">
                                            {jeLines.map((line, i) => (
                                                <tr key={i}>
                                                    <td className="px-4 py-2">
                                                        <select value={line.account} onChange={e => setJeLines(prev => prev.map((l, j) => j === i ? { ...l, account: e.target.value } : l))}
                                                            className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary">
                                                            {COA.map(a => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                                                        </select>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <Input className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary" placeholder="Description" />
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <Input type="number" value={line.debit || ''} onChange={e => setJeLines(prev => prev.map((l, j) => j === i ? { ...l, debit: parseFloat(e.target.value) || 0, credit: 0 } : l))}
                                                            className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-foreground text-xs text-right focus:outline-none focus:ring-1 focus:ring-primary" placeholder="0.00" />
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <Input type="number" value={line.credit || ''} onChange={e => setJeLines(prev => prev.map((l, j) => j === i ? { ...l, credit: parseFloat(e.target.value) || 0, debit: 0 } : l))}
                                                            className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-foreground text-xs text-right focus:outline-none focus:ring-1 focus:ring-primary" placeholder="0.00" />
                                                    </td>
                                                    <td className="px-3 py-2 text-center">
                                                        <Button variant="unstyled" size="unstyled" onClick={() => setJeLines(prev => prev.filter((_, j) => j !== i))} className="text-destructive hover:text-destructive/70 text-sm">×</Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-card/30 border-t-2 border-border">
                                            <tr>
                                                <td colSpan={2} className="px-4 py-2">
                                                    <Button variant="unstyled" size="unstyled" onClick={() => setJeLines(prev => [...prev, { account: '1000-001', desc: '', debit: 0, credit: 0 }])}
                                                        className="text-xs text-primary hover:text-primary font-semibold">+ Add Line</Button>
                                                </td>
                                                <td className="px-4 py-2 text-right font-semibold text-foreground">${totalDebit.toLocaleString()}</td>
                                                <td className={`px-4 py-2 text-right font-semibold ${totalDebit !== totalCredit ? 'text-destructive' : 'text-foreground'}`}>${totalCredit.toLocaleString()}</td>
                                                <td className="px-3 py-2 text-right">
                                                    <span className={`text-xs font-semibold ${totalDebit === totalCredit ? 'text-success' : 'text-destructive'}`}>
                                                        {totalDebit === totalCredit ? 'BALANCED ' : `Diff: $${Math.abs(totalDebit - totalCredit).toLocaleString()}`}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="flex justify-end gap-3 mt-4">
                                    <Button variant="secondary" onClick={() => setShowNewJE(false)}>Save as Draft</Button>
                                    <Button variant="primary" onClick={() => { toast.success('Journal entry submitted for approval!'); setShowNewJE(false); }}>Submit for Approval</Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardContent className="p-0">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                        <th className="text-left px-6 py-3">Entry #</th>
                                        <th className="text-left px-4 py-3">Date</th>
                                        <th className="text-left px-4 py-3">Description</th>
                                        <th className="text-center px-4 py-3">Type</th>
                                        <th className="text-right px-4 py-3">Debit</th>
                                        <th className="text-right px-4 py-3">Credit</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border-12">
                                    {JOURNAL_ENTRIES.map(je => (
                                        <tr key={je.id} className="hover:bg-card/30 transition-colors cursor-pointer">
                                            <td className="px-6 py-3 font-mono text-sm text-primary font-semibold">{je.id}</td>
                                            <td className="px-4 py-3 text-muted-foreground text-sm">{je.date}</td>
                                            <td className="px-4 py-3 font-semibold text-foreground">{je.description}</td>
                                            <td className="px-4 py-3 text-center"><Badge variant="default">{je.type}</Badge></td>
                                            <td className="px-4 py-3 text-right font-mono text-foreground">${je.debit.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right font-mono text-foreground">${je.credit.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-center"><Badge variant={statusVariant[je.status]}>{je.status}</Badge></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Trial Balance */}
            {activeTab === 'trial-balance' && (
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-3 flex flex-row justify-between items-center">
                        <h2 className="text-foreground font-display text-section-title">Trial Balance — As of March 9, 2026</h2>
                        <Button variant="secondary" size="sm" onClick={() => toast.success('Exporting Trial Balance...')}>Export PDF</Button>
                    </CardHeader>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                    <th className="text-left px-6 py-3">Account Code</th>
                                    <th className="text-left px-4 py-3">Account Name</th>
                                    <th className="text-right px-4 py-3">Debit</th>
                                    <th className="text-right px-4 py-3">Credit</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-12">
                                {COA.map(a => {
                                    const isDebit = ['asset', 'expense'].includes(a.type);
                                    return (
                                        <tr key={a.code} className="hover:bg-card/20 transition-colors">
                                            <td className="px-6 py-2 font-mono text-sm text-primary">{a.code}</td>
                                            <td className="px-4 py-2 text-foreground">{a.name}</td>
                                            <td className="px-4 py-2 text-right font-mono text-foreground">{isDebit && a.balance > 0 ? `$${Math.abs(a.balance).toLocaleString()}` : '—'}</td>
                                            <td className="px-4 py-2 text-right font-mono text-foreground">{!isDebit ? `$${Math.abs(a.balance).toLocaleString()}` : '—'}</td>
                                        </tr>
                                    );
                                })}
                                <tr className="border-t-2 border-border bg-card/50 font-semibold">
                                    <td colSpan={2} className="px-6 py-3 text-foreground">TOTAL</td>
                                    <td className="px-4 py-3 text-right font-semibold text-success">$556,000</td>
                                    <td className="px-4 py-3 text-right font-semibold text-success">$556,000</td>
                                </tr>
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {/* Balance Sheet */}
            {activeTab === 'balance-sheet' && (
                <Card className="max-w-3xl mx-auto font-mono text-sm">
                    <CardHeader className="bg-card/30 px-8 py-4 text-center">
                        <h2 className="text-foreground font-display text-section-title">BALANCE SHEET</h2>
                        <p className="text-muted-foreground text-xs mt-1">As of February 28, 2026</p>
                    </CardHeader>
                    <CardContent className="p-8">
                        {[
                            {
                                section: 'ASSETS', items: [
                                    { label: 'Current Assets', header: true },
                                    { label: 'Cash and Cash Equivalents', value: 250000 },
                                    { label: 'Accounts Receivable', value: 76000 },
                                    { label: 'Inventory', value: 95000 },
                                    { label: 'Prepaid Expenses', value: 15000 },
                                    { label: 'Total Current Assets', value: 436000, bold: true },
                                    { label: 'Fixed Assets', header: true },
                                    { label: 'Computer Equipment', value: 120000 },
                                    { label: 'Less: Accumulated Depreciation', value: -30000, negative: true },
                                    { label: 'Net Fixed Assets', value: 90000, bold: true },
                                    { label: 'TOTAL ASSETS', value: 526000, total: true },
                                ]
                            },
                            {
                                section: 'LIABILITIES & EQUITY', items: [
                                    { label: 'Current Liabilities', header: true },
                                    { label: 'Accounts Payable', value: 85000 },
                                    { label: 'Accrued Expenses', value: 35000 },
                                    { label: 'Total Current Liabilities', value: 120000, bold: true },
                                    { label: 'Long-term Liabilities', header: true },
                                    { label: 'Long-term Debt', value: 200000 },
                                    { label: 'Total Liabilities', value: 320000, bold: true },
                                    { label: 'Equity', header: true },
                                    { label: 'Share Capital', value: 100000 },
                                    { label: 'Retained Earnings', value: 106000 },
                                    { label: 'TOTAL LIABILITIES & EQUITY', value: 526000, total: true },
                                ]
                            }
                        ].map(section => (
                            <div key={section.section} className="mb-8">
                                <div className="border-b-2 border-text-400 mb-3 pb-1">
                                    <p className="font-semibold text-foreground">{section.section}</p>
                                </div>
                                {section.items.map((item, i) => (
                                    <div key={i} className={`flex justify-between py-1 ${item.header ? 'mt-3 text-muted-foreground text-xs' : ''} ${item.bold ? 'border-t border-border mt-1 pt-1 font-semibold text-foreground' : 'text-muted-foreground'} ${item.total ? 'border-t-2 border-text-100 mt-2 pt-2 font-semibold text-foreground text-base' : ''}`}>
                                        <span className={item.header ? '' : 'pl-4'}>{item.label}</span>
                                        {item.value !== undefined && (
                                            <span className={item.negative ? 'text-destructive' : ''}>
                                                {item.negative ? `($${Math.abs(item.value).toLocaleString()})` : `$${item.value.toLocaleString()}`}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

